import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import useMobileModalMode from '../../hooks/useMobileModalMode';
import BalanceInput from './BalanceInput';
import { RECONCILIATION_CURRENCIES, formatBalance, formatDaysAgo } from '../../utils/reconciliationUtils';
import { getLocalDateInputValue, formatRecordDate } from '../../utils/dateUtils';

const MISMATCH_EPSILON = 0.01;

/**
 * Yeni mutabakat kaydi (event yok) ya da gecmis kaydi duzeltme (event verilirse).
 * company: genel gorunumdeki firma satiri | event: duzenlenecek mutabakat kaydi
 */
const ReconcileModal = ({ isOpen, company, event = null, today, onClose, onDone }) => {
    const todayValue = today || getLocalDateInputValue();
    const isEdit = Boolean(event);

    const [reconciledDate, setReconciledDate] = useState(todayValue);
    const [currency, setCurrency] = useState('USD');
    const [ourBalance, setOurBalance] = useState(null);
    const [theirBalance, setTheirBalance] = useState(null);
    const [note, setNote] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useMobileModalMode(isOpen);

    useEffect(() => {
        if (!isOpen || !company) {
            return;
        }

        if (event) {
            setReconciledDate(event.reconciledDate);
            setCurrency(event.currency);
            setOurBalance(event.ourBalance);
            setTheirBalance(event.theirBalance);
            setNote(event.note || '');
        } else {
            setReconciledDate(todayValue);
            setCurrency(company.defaultCurrency || 'USD');
            setOurBalance(null);
            setTheirBalance(null);
            setNote('');
        }
    }, [isOpen, company, event, todayValue]);

    if (!isOpen || !company) {
        return null;
    }

    const bothEntered = typeof ourBalance === 'number' && typeof theirBalance === 'number';
    const difference = bothEntered ? Number((ourBalance - theirBalance).toFixed(2)) : null;
    const hasMismatch = bothEntered && Math.abs(difference) >= MISMATCH_EPSILON;
    const hasInvalidAmount = ourBalance === undefined || theirBalance === undefined;
    const noteMissing = hasMismatch && !note.trim();

    const handleSubmit = async (submitEvent) => {
        submitEvent.preventDefault();

        if (hasInvalidAmount) {
            toast.error('Geçersiz tutar. Örnek: 3.186.576,50');
            return;
        }

        if (noteMissing) {
            toast.error('Bakiyeler arasında fark var. Açıklama girin.');
            return;
        }

        const payload = {
            reconciledDate,
            currency,
            ourBalance,
            theirBalance,
            note: note.trim()
        };

        setIsSubmitting(true);
        try {
            const response = isEdit
                ? await api.patch(`/reconciliations/events/${event.id}`, payload)
                : await api.post(`/reconciliations/companies/${company.id}/reconcile`, payload);

            if (response.data.warning) {
                toast(response.data.warning, { icon: '⚠️' });
            } else {
                toast.success(response.data.message);
            }

            if (onDone) onDone();
            onClose();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Mutabakat kaydedilemedi.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const inputClass = 'w-full px-4 py-2 bg-dark-bg border border-dark-border rounded-lg focus:outline-none focus:border-primary-500 text-dark-text';
    const labelClass = 'block text-sm font-medium text-dark-muted mb-1';
    const last = company.lastEvent;

    return (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-4">
            <div className="bg-dark-card border border-dark-border w-full max-w-xl text-dark-text rounded-t-2xl shadow-2xl overflow-hidden flex flex-col max-h-[calc(100dvh-0.5rem)] sm:max-h-[90vh] sm:rounded-2xl">

                <div className="flex justify-between items-start gap-3 p-5 sm:p-6 border-b border-dark-border bg-dark-bg/40">
                    <div className="min-w-0">
                        <h2 className="text-xl font-bold">{isEdit ? 'Mutabakat Kaydını Düzenle' : 'Mutabakat Yap'}</h2>
                        <p className="truncate text-sm text-dark-muted">{company.name}</p>
                        {!isEdit && (
                            <p className="mt-1 text-xs text-dark-muted">
                                {last
                                    ? `Son mutabakat: ${formatRecordDate(last.reconciledDate)} (${formatDaysAgo(company.daysSince).toLowerCase()})`
                                    : 'Bu firma ile henüz mutabakat kaydı yok.'}
                            </p>
                        )}
                    </div>
                    <button onClick={onClose} className="p-2 text-dark-muted hover:text-dark-text hover:bg-dark-hover rounded-full transition-colors shrink-0" aria-label="Kapat">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-5 sm:p-6 overflow-y-auto">
                    <form id="reconcileForm" onSubmit={handleSubmit} className="space-y-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <div>
                                <label htmlFor="reconcileDate" className={labelClass}>Mutabakat Tarihi</label>
                                <input
                                    id="reconcileDate"
                                    type="date"
                                    value={reconciledDate}
                                    max={todayValue}
                                    onChange={(e) => setReconciledDate(e.target.value)}
                                    className={`${inputClass} cursor-pointer`}
                                    required
                                />
                            </div>
                            <div>
                                <label htmlFor="reconcileCurrency" className={labelClass}>Para Birimi</label>
                                <select
                                    id="reconcileCurrency"
                                    value={currency}
                                    onChange={(e) => setCurrency(e.target.value)}
                                    className={`${inputClass} cursor-pointer`}
                                >
                                    {RECONCILIATION_CURRENCIES.map((item) => (
                                        <option key={item} value={item}>{item}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <div>
                                <label htmlFor="reconcileOur" className={labelClass}>Bizim Bakiye</label>
                                <BalanceInput
                                    id="reconcileOur"
                                    value={isEdit ? event.ourBalance : null}
                                    onValueChange={setOurBalance}
                                    className={`${inputClass} text-right`}
                                    ariaLabel="Bizim bakiye"
                                />
                            </div>
                            <div>
                                <label htmlFor="reconcileTheir" className={labelClass}>Karşı Taraf Bakiyesi</label>
                                <BalanceInput
                                    id="reconcileTheir"
                                    value={isEdit ? event.theirBalance : null}
                                    onValueChange={setTheirBalance}
                                    className={`${inputClass} text-right`}
                                    ariaLabel="Karşı taraf bakiyesi"
                                />
                            </div>
                        </div>

                        {hasInvalidAmount && (
                            <p className="tone-danger flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
                                <AlertTriangle size={16} /> Geçersiz tutar. Örnek: 3.186.576,50
                            </p>
                        )}

                        {bothEntered && !hasMismatch && (
                            <p className="tone-success flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
                                <CheckCircle2 size={16} /> Bakiyeler tutuyor — mutabık.
                            </p>
                        )}

                        {hasMismatch && (
                            <p className="tone-danger flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
                                <AlertTriangle size={16} /> Fark: {formatBalance(difference, currency)} — kaydetmek için açıklama gerekli.
                            </p>
                        )}

                        {!bothEntered && !hasInvalidAmount && (
                            <p className="text-xs text-dark-muted">
                                Bakiyeleri boş bırakırsanız yalnızca “mutabakat yapıldı” olarak kaydedilir.
                            </p>
                        )}

                        <div>
                            <label htmlFor="reconcileNote" className={labelClass}>
                                Not / Fark Gerekçesi {hasMismatch && <span className="text-red-400">*</span>}
                            </label>
                            <textarea
                                id="reconcileNote"
                                rows="3"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                className={`${inputClass} ${noteMissing ? 'border-red-500/60' : ''}`}
                                placeholder="Örn: Kur farkı, 12.08 tarihli fatura henüz kesilmedi"
                            />
                        </div>
                    </form>
                </div>

                <div className="sticky bottom-0 z-10 p-4 sm:p-6 border-t border-dark-border bg-dark-card/95 flex justify-end gap-3 pb-safe-offset">
                    <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-dark-muted hover:text-dark-text hover:bg-dark-hover transition-colors">
                        Vazgeç
                    </button>
                    <button
                        type="submit"
                        form="reconcileForm"
                        disabled={isSubmitting || hasInvalidAmount}
                        className="px-5 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? 'Kaydediliyor...' : (isEdit ? 'Güncelle' : 'Mutabakatı Kaydet')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReconcileModal;
